import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";
import RichTextEditor from "@/components/RichTextEditor";

const empty = {
  title: "", slug: "", heading: "", content: "",
  active: false, show_in_footer: false, footer_label: "", footer_sort_order: 0,
  indexable: true, seo_title: "", seo_description: "", og_title: "", og_description: "",
};

export default function PageEdit() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (id) api.get(`/admin/pages/${id}`).then((r) => setP({ ...empty, ...r.data }))
      .catch(() => { toast.error("Page not found"); nav("/admin/pages"); });
  }, [id]);

  const upd = (k, v) => setP((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!p.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      await api.put(`/admin/pages/${id}`, p);
      toast.success("Page saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6 gap-3">
        <h1 className="font-display text-2xl sm:text-3xl text-gold">Edit Page</h1>
        {p.active && (
          <a href={`/page/${p.slug}`} target="_blank" rel="noopener noreferrer"
             data-testid="page-view-storefront"
             className="btn-outline text-xs inline-flex items-center gap-2"><ExternalLink size={14} /> View</a>
        )}
      </div>

      <div className="space-y-5 curio-card p-5 sm:p-6">
        <Row label="Title *"><input value={p.title} onChange={(e) => upd("title", e.target.value)} className="input-vintage" data-testid="pe-page-title" /></Row>
        <Row label="URL Slug"><input value={p.slug} onChange={(e) => upd("slug", e.target.value)} className="input-vintage" data-testid="pe-page-slug" placeholder="privacy" />
          <p className="text-[10px] text-parchment/50 mt-1">Lives at <span className="font-mono">/page/{p.slug || "…"}</span></p>
        </Row>
        <Row label="Page heading (falls back to title)">
          <input value={p.heading || ""} onChange={(e) => upd("heading", e.target.value)} className="input-vintage" />
        </Row>
        <Row label="Content">
          <RichTextEditor value={p.content || ""} onChange={(html) => upd("content", html)} data-testid="pe-page-content" />
        </Row>

        <div className="border-t border-brass-soft pt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Toggle checked={p.active} onChange={(v) => upd("active", v)} label="Active (publish page)" testid="pe-page-active" />
          <Toggle checked={p.show_in_footer} onChange={(v) => upd("show_in_footer", v)} label="Show in footer" testid="pe-page-footer" />
          <Toggle checked={p.indexable} onChange={(v) => upd("indexable", v)} label="Allow search engines (indexable)" testid="pe-page-index" />
        </div>

        <Row label="Footer link label (falls back to title)">
          <input value={p.footer_label || ""} onChange={(e) => upd("footer_label", e.target.value)} className="input-vintage" data-testid="pe-page-footer-label" placeholder="Privacy" />
        </Row>

        <details className="border-t border-brass-soft pt-4">
          <summary className="font-caption text-xs text-brass cursor-pointer">SEO & Social Sharing</summary>
          <div className="space-y-3 mt-3">
            <Row label="SEO Title"><input value={p.seo_title || ""} onChange={(e) => upd("seo_title", e.target.value)} className="input-vintage" /></Row>
            <Row label="Meta Description"><textarea rows={2} value={p.seo_description || ""} onChange={(e) => upd("seo_description", e.target.value)} className="input-vintage" /></Row>
            <Row label="Social Share Title"><input value={p.og_title || ""} onChange={(e) => upd("og_title", e.target.value)} className="input-vintage" /></Row>
            <Row label="Social Share Description"><textarea rows={2} value={p.og_description || ""} onChange={(e) => upd("og_description", e.target.value)} className="input-vintage" /></Row>
          </div>
        </details>

        <div className="flex gap-3 pt-4 border-t border-brass-soft">
          <button onClick={save} disabled={saving} className="btn-gold" data-testid="pe-page-save">{saving ? "Saving…" : "Save Page"}</button>
          <button onClick={() => nav("/admin/pages")} className="btn-outline">Cancel</button>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, children }) => (
  <div>
    <label className="font-caption text-[10px] text-brass block mb-1">{label}</label>
    {children}
  </div>
);
const Toggle = ({ checked, onChange, label, testid }) => (
  <label className="flex items-center gap-2 text-sm text-parchment/80">
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />
    {label}
  </label>
);
